import { platforms, products as rawProducts } from '../mock/mock';

// Products expose rate/min/max/smmId now (no fixed variants). The frontend
// computes prices live from the entered quantity.
export const products = rawProducts;

// Coefficient par défaut (utilisé pour toute plateforme non listée)
export const DEFAULT_COEFFICIENT = 2;

// Coefficients de tarification par catégorie (plateforme).
// Formule officielle : Prix = (qty / 1000) × rate × coefficient(plateforme)
// La clé correspond à l'`id` de la plateforme dans mock.js.
export const COEFFICIENTS = {
  // 📱 Réseaux sociaux très concurrentiels
  tiktok: 1.5,
  instagram: 1.5,
  youtube: 1.7,
  facebook: 1.7,
  twitter: 1.7,      // X (Twitter)
  telegram: 1.8,
  discord: 1.8,
  spotify: 1.8,      // user l'a rangé sous "concurrentiels"
  twitch: 1.8,
  snapchat: 1.8,
  threads: 1.8,
  pinterest: 1.8,
  reddit: 1.8,

  // 🎵 Plateformes musicales
  applemusic: 2,
  deezer: 2,
  soundcloud: 2,
  audiomack: 2,
  boomplay: 2,

  // 💼 Services spécialisés
  linkedin: 2.5,
  google: 3,         // Google Reviews & Google Maps (les deux ×3)
  trustpilot: 3.5,
  traffic: 4,        // SEO / Backlinks / Traffic Web (regroupés)
  coinmarketcap: 3,
  steam: 3,
  rumble: 2.5,
  vimeo: 2.5
};

// Retourne le coefficient applicable pour une plateforme donnée
export const getCoefficient = (platformKey) => {
  if (!platformKey) return DEFAULT_COEFFICIENT;
  const c = COEFFICIENTS[platformKey];
  return typeof c === 'number' && c > 0 ? c : DEFAULT_COEFFICIENT;
};

// Marge commerciale appliquée sur le prix de vente final (+30%).
export const SALE_MARKUP = 1.30;

// Formule officielle SociBoost :
// Prix total = (Quantité / 1000) × Rate fournisseur SMMHype × Coefficient(catégorie) × 1,30
// Premier palier : tant que le prix brut n'atteint pas 1 €, on facture 1 € plat.
// Le client peut donc commander n'importe quelle quantité entre 100 et le
// seuil où le calcul dépasse 1 € ; au-delà, tarification progressive .49/.99.
export const computeLivePrice = (rate, qty, coef = DEFAULT_COEFFICIENT) => {
  const r = Number(rate) || 0;
  const q = Number(qty) || 0;
  const c = Number(coef) || DEFAULT_COEFFICIENT;
  const raw = (q / 1000) * r * c * SALE_MARKUP;
  if (!Number.isFinite(raw) || raw <= 0) return 0;
  // Premier palier plat à 1 €
  if (raw < 1.0) return 1.0;
  // Au-delà : arrondi commercial vers .49 ou .99
  const rounded = Math.round((raw + 0.01) * 2) / 2 - 0.01;
  return Math.max(1.0, Number(rounded.toFixed(2)));
};

// Helper pratique : calcule le prix pour un produit + quantité en utilisant
// automatiquement le coefficient de sa plateforme.
export const computePriceForProduct = (product, qty) => {
  if (!product) return 0;
  const coef = getCoefficient(product.platformKey);
  return computeLivePrice(product.rate, qty, coef);
};

// Quantité minimum applicable pour un service :
// - au moins 100 (règle SociBoost pour le palier 1 €)
// - jamais en dessous du min imposé par SMMHype
// - jamais au-dessus du max SMMHype
export const getEffectiveMinQty = (product) => {
  if (!product) return 100;
  const smmMin = Number(product.min || 1);
  const smmMax = Number(product.max || 1_000_000);
  return Math.min(smmMax, Math.max(100, smmMin));
};

// Prix vitrine "à partir de X € / 1000 unités" (comme les panels SMM pro).
// = rate × coefficient(catégorie), arrondi half-up sur 2 décimales.
export const pricePer1000 = (product) => {
  if (!product) return 0;
  const coef = getCoefficient(product.platformKey);
  return computeLivePrice(product.rate, 1000, coef);
};

// Détecte le libellé d'unité à afficher (abonnés / likes / vues / …)
// à partir du nom du service. Fallback: "unités".
export const getUnitLabel = (name) => {
  const n = (name || '').toLowerCase();
  if (n.includes('abonn') || n.includes('follower') || n.includes('subscrib') || n.includes('fan')) return 'abonnés';
  if (n.includes('member') || n.includes('membre')) return 'membres';
  if (n.includes('like') || n.includes('heart') || n.includes('reaction')) return 'likes';
  if (n.includes('play') || n.includes('stream')) return 'plays';
  if (n.includes('listener') || n.includes('auditeur')) return 'auditeurs';
  if (n.includes('view') || n.includes('vue') || n.includes('watch')) return 'vues';
  if (n.includes('partage') || n.includes('share') || n.includes('retweet') || n.includes('repost')) return 'partages';
  if (n.includes('comment')) return 'commentaires';
  if (n.includes('avis') || n.includes('review') || n.includes('rating')) return 'avis';
  if (n.includes('vote') || n.includes('upvote')) return 'votes';
  if (n.includes('save') || n.includes('bookmark')) return 'saves';
  if (n.includes('impression')) return 'impressions';
  if (n.includes('chatter') || n.includes('chat')) return 'chatters';
  if (n.includes('viewer')) return 'viewers';
  if (n.includes('boost')) return 'boosts';
  if (n.includes('wishlist')) return 'wishlists';
  if (n.includes('connection') || n.includes('connexion')) return 'connexions';
  return 'unités';
};

// Flatten all products across platforms with references to platform info.
export const allProducts = Object.entries(products).flatMap(([platformKey, arr]) =>
  arr.map(p => ({
    ...p,
    platformKey,
    platform: platforms.find(pl => pl.id === platformKey)
  }))
);

export const findProductById = (id) => allProducts.find(p => p.id === id) || null;

export const findPlatform = (slugOrId) =>
  platforms.find(p => p.slug === slugOrId || p.id === slugOrId) || null;

// Search: fuzzy contains, case-insensitive, ranks exact-start matches higher.
export const searchCatalog = (q, limit = 12) => {
  const query = (q || '').trim().toLowerCase();
  if (!query) return { platforms: [], products: [] };

  const scored = (str) => {
    const s = (str || '').toLowerCase();
    if (s === query) return 100;
    if (s.startsWith(query)) return 80;
    if (s.includes(` ${query}`)) return 60;
    if (s.includes(query)) return 40;
    return 0;
  };

  const platMatches = platforms
    .map(p => ({ ...p, _s: Math.max(scored(p.name), scored(p.slug), scored(p.desc)) }))
    .filter(p => p._s > 0)
    .sort((a, b) => b._s - a._s)
    .slice(0, 6);

  const prodMatches = allProducts
    .map(p => ({ ...p, _s: Math.max(scored(p.name), scored(p.platform?.name)) }))
    .filter(p => p._s > 0)
    .sort((a, b) => b._s - a._s)
    .slice(0, limit);

  return { platforms: platMatches, products: prodMatches };
};
